import { useMemo } from 'react';
import { useAuth0, type User } from '@auth0/auth0-react';
import {
  CLAIM_AIRLINE,
  CLAIM_OPERATOR_URN,
  CLAIM_ROLE,
  CLAIM_SCOPE,
  isRole,
  normalizeScopeEntries,
  type Role,
  type ScopeEntry,
} from '@nexa/auth';
import { useCachedAccessSnapshot } from '@/lib/access/snapshot';

// The token-claim reader behind `useSession()` (docs/ui/auth0-frontend-tds.md §5.4: raw
// claim keys are read here and in `use-session.ts` only, never at call sites).
//
// Every hook here is TOLERANT — no throw, `null` for "not signed in / not present" — so
// chrome and access code can call them in any state (a member with no role, the
// pre-login shell). Split from `use-session.ts` on purpose: ~20 component specs replace
// that module with a `useSession`-only mock, and access code must keep working under them.
//
// Scope (users-roles-permissions R5): the claim may be a LIST. It goes through
// `normalizeScopeEntries`, which fails closed — absent → `null` (tenant-wide, as before),
// `[]` → no airports, entries → bounded, anything malformed → `[]`. An array is never "no
// scope" (brief §3, defect 1).

export interface ISessionClaims {
  airline: string | null;
  role: Role | null;
  operatorUrn: string | null;
  displayName: string;
  /** Normalized scope claim — `null` when absent (tenant-wide), `[]` when unreadable. */
  scope: ScopeEntry[] | null;
}

export function readClaims(user: User): ISessionClaims {
  const airline = user[CLAIM_AIRLINE];
  const role = user[CLAIM_ROLE];
  const operatorUrn = user[CLAIM_OPERATOR_URN];
  return {
    airline: typeof airline === 'string' && airline ? airline : null,
    role: isRole(role) ? role : null,
    operatorUrn: typeof operatorUrn === 'string' && operatorUrn ? operatorUrn : null,
    displayName: user.name ?? user.email ?? user.sub ?? 'Operator',
    scope: normalizeScopeEntries(user[CLAIM_SCOPE]),
  };
}

/** The token's claims, or `null` when not signed in. */
export function useSessionClaims(): ISessionClaims | null {
  const { user, isAuthenticated } = useAuth0();
  return useMemo(
    () => (isAuthenticated && user ? readClaims(user) : null),
    [isAuthenticated, user],
  );
}

/** The scope claim, normalized and fail-closed (`null` = absent = tenant-wide). */
export function useSessionScope(): ScopeEntry[] | null {
  return useSessionClaims()?.scope ?? null;
}

/**
 * The session's legacy role, or `null`. Prefers the BFF-derived role of the cached
 * `GET /v1/me` (M1 §8.4 — a member may hold roles in the access store and no role claim at
 * all); the claim answers only while `/v1/me` is unknown. Passive: never fetches.
 */
export function useSessionRole(): Role | null {
  const claims = useSessionClaims();
  const { snapshot } = useCachedAccessSnapshot(claims?.scope ?? null);
  if (snapshot) return snapshot.legacyRole;
  return claims?.role ?? null;
}

/** Display identity for chrome (avatar menu, "Sin acceso asignado"). */
export function useSessionIdentity(): { displayName: string; operatorUrn: string | null } | null {
  const claims = useSessionClaims();
  if (!claims) return null;
  return { displayName: claims.displayName, operatorUrn: claims.operatorUrn };
}
